import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { PLAN_LIMITS } from "@/hooks/useSubscriptionManager";
import { UserType } from "@/types/user";

/**
 * Component that displays a welcome message after successful payment
 * Shows plan-specific details and report limits
 */
const PaymentSuccessMessage = () => {
  const [show, setShow] = useState(false);
  const [planType, setPlanType] = useState<UserType | null>(null);
  const [reportLimit, setReportLimit] = useState<number | null>(null);
  const [planName, setPlanName] = useState<string>("");

  useEffect(() => {
    // Check if the user was redirected from payment success
    const paymentSuccess = sessionStorage.getItem("payment_success");

    if (paymentSuccess === "true") {
      // Get user details to show appropriate welcome message
      const getUserDetails = async () => {
        try {
          // Get the current user
          const {
            data: { user },
          } = await supabase.auth.getUser();

          if (user) {
            // Get the user's profile to determine plan type
            const { data: profileData } = await supabase
              .from("profiles")
              .select("user_type")
              .eq("id", user.id)
              .single();

            if (profileData) {
              const userPlanType = profileData.user_type as UserType;
              setPlanType(userPlanType);
              setReportLimit(
                PLAN_LIMITS[userPlanType as keyof typeof PLAN_LIMITS] || 0
              );

              // Set a user-friendly plan name
              switch (userPlanType) {
                case "starter":
                  setPlanName("Starter Plan");
                  break;
                case "pro":
                  setPlanName("Pro Plan");
                  break;
                case "premium":
                  setPlanName("Premium Plan");
                  break;
                case "free":
                  setPlanName("Free Plan");
                  break;
                default:
                  setPlanName("Basic Access");
              }

              // Show the message
              setShow(true);
            }
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      };

      getUserDetails();

      // Remove the flag so the message doesn't show again on refresh
      sessionStorage.removeItem("payment_success");
    }
  }, []);

  const handleDismiss = () => {
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md"
        >
          <div className="bg-gradient-to-br from-green-900/90 to-emerald-900/90 backdrop-blur-md p-6 rounded-xl border border-green-500/30 shadow-xl shadow-green-500/20 mx-4">
            <div className="absolute top-3 right-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-8 w-8 rounded-full hover:bg-green-800/50 text-green-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="bg-green-500/20 p-2 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Payment Successful!
              </h3>
            </div>

            <div className="mb-4">
              <p className="text-green-100 mb-2">
                Thank you for your purchase. Your account has been upgraded to:
              </p>
              <div className="flex items-center gap-2 bg-green-500/20 p-3 rounded-lg">
                <Sparkles className="h-5 w-5 text-yellow-300" />
                <span className="font-bold text-white">{planName}</span>
              </div>
            </div>

            <div className="bg-green-500/10 p-4 rounded-lg mb-4 border border-green-500/20">
              <p className="text-white font-medium text-center">
                {reportLimit === 1
                  ? "You can now generate 1 report!"
                  : `You can now generate ${reportLimit} reports per month!`}
              </p>
            </div>

            <div className="text-center">
              <Button
                onClick={handleDismiss}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
              >
                Got it!
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentSuccessMessage;
